import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../shared/hooks/sharedHooks";
import colors from "../../shared/themes/colors";
import MachineLogoIcon from "../../shared/assets/icons/MachineLogoIcon";
import Loader from "../../shared/components/Loader";
import ActionToast from "../../shared/components/ActionToast";
import FormNotificationMessage from "../../shared/components/FormNotificationMessage";
import { passwordRegex } from "../../shared/validators";
import { ResetPasswordModel, ResetPasswordFormValues } from "../models/AccountModels";
import { refreshAccount } from "../accountSlice";
import {
  resetUserPassword,
  setOpenSnackbar,
  togglePasswordVisibility,
  toggleConfirmPasswordVisibility,
  showNotification,
  setFormNotificationMessage,
} from "../resetPasswordSlice";
import { handleLogin } from "../loginSlice";
import { NotificationMessage } from "../../shared/models/models";
import useTranslation from "../../shared/hooks/useTranslation";
import useDefaultRedirect from "../hooks/useDefaultRedirect";

const ResetPassword = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { translate } = useTranslation("account");
  const { translate: tCommon } = useTranslation("common");
  const { defaultRoute } = useDefaultRedirect();

  const { hash } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    isLoading,
    formNotificationMessage,
    openSnackbar,
    alertMessage,
    severity,
    showPassword,
    showConfirmPassword,
  } = useAppSelector((state) => state.resetPassword);
  const { user } = useAppSelector((state) => state.account);
  const { isLoading: isLoadingLogin } = useAppSelector((state) => state.login);

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>();

  const watchedFields = watch(["newPassword", "confirmPassword"]);
  const isFormValid = watchedFields[0] && watchedFields[1] &&
    watchedFields[0].trim() !== "" && watchedFields[1].trim() !== "";

  const consumeUrl = () => {
    const paramsString = hash.replace("#", "").replaceAll("+", "%2B");
    const params = new URLSearchParams(paramsString);

    const formValues = {
      userName: params.get("userName") || "",
      token: params.get("token") || "",
    };
    reset(formValues);
  };

  const handleClickShowPassword = () => {
    dispatch(togglePasswordVisibility());
  };

  const handleClickShowConfirmPassword = () => {
    dispatch(toggleConfirmPasswordVisibility());
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const validateConfirmPassword = (value?: string) => {
    const { newPassword } = getValues();
    return newPassword === value || translate("passwordsDoNotMatch");
  };

  const onSubmit: SubmitHandler<ResetPasswordFormValues> = async (model: ResetPasswordFormValues) => {
    const resetModel = model as ResetPasswordModel;

    try {
      const resetResult = await dispatch(resetUserPassword(resetModel));

      if (resetUserPassword.fulfilled.match(resetResult)) {
        const loginResult = await dispatch(handleLogin({
          userName: resetModel.userName,
          password: resetModel.newPassword
        }));

        if (handleLogin.fulfilled.match(loginResult)) {
          dispatch(showNotification({
            openSnackbar: true,
            severity: "success",
            alertMessage: "Password changed successfully!"
          }));
          dispatch(refreshAccount(null));
          navigate(defaultRoute(), { replace: true });
        }
      }
    } catch (error) {
      dispatch(setFormNotificationMessage(NotificationMessage.error(error)));
    }
  };

  useEffect(() => {
    consumeUrl();
  }, []);

  return (
    <>
      <ActionToast
        open={openSnackbar}
        variant={severity === "error" ? "error" : "success"}
        message={alertMessage}
        onClose={() => dispatch(setOpenSnackbar(false))}
        duration={6000}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingY: isMobile ? "32px" : "64px",
          backgroundColor: colors.secondary.gray98,
        }}
      >
        <Card
          sx={{
            maxWidth: "600px",
            width: "100%",
            margin: "16px",
            display: "flex",
            flexDirection: "column",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            padding: isMobile ? "32px 16px 32px" : "64px 48px 64px",
            touchAction: "manipulation",
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            userSelect: "none"
          }}
        >
          {isLoading || isLoadingLogin || user ? (
            <Box
              sx={{
                width: '460px',
                minHeight: '420px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Loader />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <MachineLogoIcon />
                <Box
                  sx={{
                    fontFamily: "Work Sans",
                    fontSize: isMobile ? "24px" : "36px",
                    fontWeight: "500",
                    lineHeight: isMobile ? "32px" : "42px",
                    letterSpacing: "-0.072px",
                  }}
                >
                  {translate("createYourPassword")}
                </Box>
                <Box className="text-18-regular mt-12" sx={{ color: colors.secondary.black, fontSize: isMobile ? "16px" : "18px" }}>
                  {translate("passwordInfo")}
                </Box>
              </Box>

              <CardContent>
                {formNotificationMessage && (
                  <Box className="mb-3">
                    <FormNotificationMessage {...formNotificationMessage} />
                  </Box>
                )}

                <Box
                  component="form"
                  id="resetPasswordForm"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <Box className="flex flex-col gap-2">
                    <Box component="input" type="hidden" {...register("userName")} />
                    <Box component="input" type="hidden" {...register("token")} />

                    <Box className="flex flex-col mb-5">
                      <InputLabel>{translate("password")}</InputLabel>
                      <TextField
                        type={showPassword ? "text" : "password"}
                        className="w-full"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                              >
                                {showPassword ? <VisibilityOutlined fontSize="small" /> : <VisibilityOffOutlined fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        {...register("newPassword", {
                          required: tCommon("forms.required"),
                          pattern: { value: passwordRegex, message: translate("passwordRequirements") },
                        })}
                        error={!!errors.newPassword}
                        helperText={
                          errors.newPassword
                            ? errors.newPassword.message || translate("fieldInvalid")
                            : ""
                        }
                      />
                    </Box>

                    <Box className="flex flex-col">
                      <InputLabel required>{translate("repeatPassword")}</InputLabel>
                      <TextField
                        type={showConfirmPassword ? "text" : "password"}
                        className="w-full"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowConfirmPassword}
                                onMouseDown={handleMouseDownPassword}
                              >
                                {showConfirmPassword ? <VisibilityOutlined fontSize="small" /> : <VisibilityOffOutlined fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        {...register("confirmPassword", { validate: validateConfirmPassword })}
                        error={!!errors.confirmPassword}
                        helperText={
                          errors.confirmPassword
                            ? errors.confirmPassword.message || translate("fieldInvalid")
                            : ""
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              </CardContent>

              <CardActions sx={{
                width: "100%",
                padding: "0 16px",
                mt: 5,
                "& .MuiButton-root": {
                  minHeight: isMobile ? "48px" : "auto",
                  fontSize: isMobile ? "16px" : "14px"
                }
              }}>
                <Button
                  fullWidth
                  form="resetPasswordForm"
                  type="submit"
                  variant="contained"
                  disabled={!isFormValid || isLoading}
                  onClick={(e) => {
                    if (isFormValid && !isLoading) {
                      e.preventDefault();
                      handleSubmit(onSubmit)();
                    }
                  }}
                  sx={{
                    minHeight: isMobile ? "48px" : "36px",
                    fontSize: isMobile ? "16px" : "14px",
                    touchAction: "manipulation"
                  }}
                >
                  {translate("resetPassword")}
                </Button>
              </CardActions>
            </>
          )}
        </Card>
      </Box>
    </>
  );
};

export default ResetPassword;